import type { GraphDocument, NodeRecord, EdgeRecord } from '@/domain/funnel/graph-document';
import { SimulationGraphExtensionSchema } from '@/domain/simulation/contracts';
import { PresentationGraphExtensionSchema } from '@/domain/presentation/contracts';
import { DocumentationGraphExtensionSchema } from '@/domain/documentation/contracts';

/**
 * docs/architecture/graph-schema.md §8/§9 — the single canonical registry of
 * graph-internal references and the shared collect/validate/remap functions.
 * SPEC-010 (template instantiation) and SPEC-013 (import) are the primary
 * consumers; neither may maintain its own reference list or remapper
 * (TEMPLATE-REQ-009/plan §15). Future SPECs that register a top-level
 * extension with node/edge references (presentation, documentation, ...)
 * extend `EXTENSION_REMAPPERS`/`EXTENSION_COLLECTORS` below rather than
 * inventing a parallel mechanism.
 */

export interface ReferenceInventory {
  nodeIds: Set<string>;
  edgeIds: Set<string>;
  /** Edge ids whose source and/or target does not resolve to a known node id. */
  danglingEdgeIds: string[];
  /** Duplicate node ids (should be empty in a valid document). */
  duplicateNodeIds: string[];
  /** Duplicate edge ids (should be empty in a valid document). */
  duplicateEdgeIds: string[];
}

export interface ReferenceValidationResult {
  valid: boolean;
  errors: string[];
}

export interface RemapContext {
  nodeIdMap: ReadonlyMap<string, string>;
  edgeIdMap: ReadonlyMap<string, string>;
  createId: () => string;
}

type ExtensionRemapper = (value: unknown, ctx: RemapContext) => unknown;

/**
 * Remaps the SPEC-009 `simulation` extension's internal node/edge-id-keyed
 * references (SIM overrides, target.nodeId) onto the new ids. Entries whose
 * key no longer resolves to a known node/edge are dropped rather than kept
 * dangling (graph-schema.md §9: remap result must validate cleanly).
 */
function remapSimulationExtension(value: unknown, ctx: RemapContext): unknown {
  const parsed = SimulationGraphExtensionSchema.safeParse(value);
  if (!parsed.success) return value;
  const extension = parsed.data;
  const scenarioIdMap = new Map(
    extension.scenarios.map((scenario) => [scenario.id, ctx.createId()]),
  );

  const scenarios = extension.scenarios.map((scenario) => {
    const nodeOverrides: Record<string, (typeof scenario.overrides.nodeOverrides)[string]> = {};
    for (const [nodeId, override] of Object.entries(scenario.overrides.nodeOverrides)) {
      const nextId = ctx.nodeIdMap.get(nodeId);
      if (nextId) nodeOverrides[nextId] = override;
    }
    const edgeOverrides: Record<string, (typeof scenario.overrides.edgeOverrides)[string]> = {};
    for (const [edgeId, override] of Object.entries(scenario.overrides.edgeOverrides)) {
      const nextId = ctx.edgeIdMap.get(edgeId);
      if (nextId) edgeOverrides[nextId] = override;
    }
    const target =
      scenario.target && scenario.target.nodeId
        ? ctx.nodeIdMap.has(scenario.target.nodeId)
          ? { ...scenario.target, nodeId: ctx.nodeIdMap.get(scenario.target.nodeId) }
          : { ...scenario.target, nodeId: undefined }
        : scenario.target;

    return {
      ...scenario,
      id: scenarioIdMap.get(scenario.id)!,
      overrides: { nodeOverrides, edgeOverrides },
      target,
    };
  });

  return {
    ...extension,
    scenarios,
    activeScenarioId:
      extension.activeScenarioId && scenarioIdMap.has(extension.activeScenarioId)
        ? scenarioIdMap.get(extension.activeScenarioId)!
        : null,
  };
}

/**
 * SPEC-011 §8 — strategic-note and highlight identities get fresh ids
 * (never carried over where independence is required); their node/edge/
 * note-id references are remapped the same way as scenario overrides, and
 * a reference that no longer resolves is dropped rather than left dangling.
 */
function remapPresentationExtension(value: unknown, ctx: RemapContext): unknown {
  const parsed = PresentationGraphExtensionSchema.safeParse(value);
  if (!parsed.success) return value;
  const extension = parsed.data;

  const noteIdMap = new Map(extension.strategicNotes.map((note) => [note.id, ctx.createId()]));
  const strategicNotes = extension.strategicNotes.map((note) => ({
    ...note,
    id: noteIdMap.get(note.id)!,
    nodeIds: note.nodeIds.filter((id) => ctx.nodeIdMap.has(id)).map((id) => ctx.nodeIdMap.get(id)!),
  }));

  const highlights = extension.highlights.map((highlight) => ({
    ...highlight,
    id: ctx.createId(),
    nodeIds: highlight.nodeIds
      .filter((id) => ctx.nodeIdMap.has(id))
      .map((id) => ctx.nodeIdMap.get(id)!),
    edgeIds: highlight.edgeIds
      .filter((id) => ctx.edgeIdMap.has(id))
      .map((id) => ctx.edgeIdMap.get(id)!),
    noteId:
      highlight.noteId && noteIdMap.has(highlight.noteId)
        ? noteIdMap.get(highlight.noteId)
        : undefined,
  }));

  return { ...extension, strategicNotes, highlights };
}

/** SPEC-017 — persisted SOP/checklist overlays reference canonical nodes;
 * regenerate checklist identities so imported/duplicated documents remain independent. */
function remapDocumentationExtension(value: unknown, ctx: RemapContext): unknown {
  const parsed = DocumentationGraphExtensionSchema.safeParse(value);
  if (!parsed.success) return value;
  return {
    ...parsed.data,
    sop: parsed.data.sop
      ? {
          ...parsed.data.sop,
          steps: parsed.data.sop.steps
            ?.filter((step) => ctx.nodeIdMap.has(step.nodeId))
            .map((step) => ({ ...step, nodeId: ctx.nodeIdMap.get(step.nodeId)! })),
        }
      : undefined,
    checklistItems: parsed.data.checklistItems?.map((item) => ({
      ...item,
      id: ctx.createId(),
      stepNodeId:
        item.stepNodeId && ctx.nodeIdMap.has(item.stepNodeId)
          ? ctx.nodeIdMap.get(item.stepNodeId)
          : undefined,
    })),
  };
}

const EXTENSION_REMAPPERS: Record<string, ExtensionRemapper> = {
  simulation: remapSimulationExtension,
  presentation: remapPresentationExtension,
  documentation: remapDocumentationExtension,
};

export function collectGraphReferences(document: GraphDocument): ReferenceInventory {
  const nodeIds = new Set<string>();
  const duplicateNodeIds: string[] = [];
  for (const node of document.nodes as NodeRecord[]) {
    if (nodeIds.has(node.id)) duplicateNodeIds.push(node.id);
    nodeIds.add(node.id);
  }

  const edgeIds = new Set<string>();
  const duplicateEdgeIds: string[] = [];
  const danglingEdgeIds: string[] = [];
  for (const edge of document.edges as EdgeRecord[]) {
    if (edgeIds.has(edge.id)) duplicateEdgeIds.push(edge.id);
    edgeIds.add(edge.id);
    if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) {
      danglingEdgeIds.push(edge.id);
    }
  }

  return { nodeIds, edgeIds, danglingEdgeIds, duplicateNodeIds, duplicateEdgeIds };
}

export function validateGraphReferences(document: GraphDocument): ReferenceValidationResult {
  const inventory = collectGraphReferences(document);
  const errors: string[] = [];
  if (inventory.duplicateNodeIds.length > 0) {
    errors.push(`Duplicate node id(s): ${inventory.duplicateNodeIds.join(', ')}`);
  }
  if (inventory.duplicateEdgeIds.length > 0) {
    errors.push(`Duplicate edge id(s): ${inventory.duplicateEdgeIds.join(', ')}`);
  }
  if (inventory.danglingEdgeIds.length > 0) {
    errors.push(
      `Dangling edge(s) referencing an unknown node: ${inventory.danglingEdgeIds.join(', ')}`,
    );
  }
  validateSimulationReferences(document.simulation, inventory, errors);
  validatePresentationReferences(document.presentation, inventory, errors);
  validateDocumentationReferences(document.documentation, inventory, errors);
  return { valid: errors.length === 0, errors };
}

function validateDocumentationReferences(
  value: unknown,
  inventory: ReferenceInventory,
  errors: string[],
) {
  if (value === undefined) return;
  const parsed = DocumentationGraphExtensionSchema.safeParse(value);
  if (!parsed.success) {
    errors.push('Invalid documentation extension.');
    return;
  }
  for (const step of parsed.data.sop?.steps ?? []) {
    if (!inventory.nodeIds.has(step.nodeId))
      errors.push(`Documentation SOP step references unknown node ${step.nodeId}.`);
  }
  const itemIds = new Set<string>();
  for (const item of parsed.data.checklistItems ?? []) {
    if (itemIds.has(item.id)) errors.push(`Duplicate documentation checklist item id: ${item.id}`);
    itemIds.add(item.id);
    if (item.stepNodeId && !inventory.nodeIds.has(item.stepNodeId))
      errors.push(
        `Documentation checklist item ${item.id} references unknown node ${item.stepNodeId}.`,
      );
  }
}

function validateSimulationReferences(
  value: unknown,
  inventory: ReferenceInventory,
  errors: string[],
) {
  if (value === undefined) return;
  const parsed = SimulationGraphExtensionSchema.safeParse(value);
  if (!parsed.success) {
    errors.push('Invalid simulation extension.');
    return;
  }
  const scenarioIds = new Set<string>();
  for (const scenario of parsed.data.scenarios) {
    if (scenarioIds.has(scenario.id)) errors.push(`Duplicate scenario id: ${scenario.id}`);
    scenarioIds.add(scenario.id);
    for (const nodeId of Object.keys(scenario.overrides.nodeOverrides)) {
      if (!inventory.nodeIds.has(nodeId)) {
        errors.push(`Scenario ${scenario.id} references unknown node ${nodeId}.`);
      }
    }
    for (const edgeId of Object.keys(scenario.overrides.edgeOverrides)) {
      if (!inventory.edgeIds.has(edgeId)) {
        errors.push(`Scenario ${scenario.id} references unknown edge ${edgeId}.`);
      }
    }
    if (scenario.target?.nodeId && !inventory.nodeIds.has(scenario.target.nodeId)) {
      errors.push(
        `Scenario ${scenario.id} target references unknown node ${scenario.target.nodeId}.`,
      );
    }
  }
  if (parsed.data.activeScenarioId && !scenarioIds.has(parsed.data.activeScenarioId)) {
    errors.push(`Active scenario references unknown scenario ${parsed.data.activeScenarioId}.`);
  }
}

function validatePresentationReferences(
  value: unknown,
  inventory: ReferenceInventory,
  errors: string[],
) {
  if (value === undefined) return;
  const parsed = PresentationGraphExtensionSchema.safeParse(value);
  if (!parsed.success) {
    errors.push('Invalid presentation extension.');
    return;
  }
  const noteIds = new Set<string>();
  for (const note of parsed.data.strategicNotes) {
    if (noteIds.has(note.id)) errors.push(`Duplicate strategic note id: ${note.id}`);
    noteIds.add(note.id);
    for (const nodeId of note.nodeIds) {
      if (!inventory.nodeIds.has(nodeId)) {
        errors.push(`Strategic note ${note.id} references unknown node ${nodeId}.`);
      }
    }
  }
  const highlightIds = new Set<string>();
  for (const highlight of parsed.data.highlights) {
    if (highlightIds.has(highlight.id)) errors.push(`Duplicate highlight id: ${highlight.id}`);
    highlightIds.add(highlight.id);
    for (const nodeId of highlight.nodeIds) {
      if (!inventory.nodeIds.has(nodeId)) {
        errors.push(`Highlight ${highlight.id} references unknown node ${nodeId}.`);
      }
    }
    for (const edgeId of highlight.edgeIds) {
      if (!inventory.edgeIds.has(edgeId)) {
        errors.push(`Highlight ${highlight.id} references unknown edge ${edgeId}.`);
      }
    }
    if (highlight.noteId && !noteIds.has(highlight.noteId)) {
      errors.push(
        `Highlight ${highlight.id} references unknown strategic note ${highlight.noteId}.`,
      );
    }
  }
}

export interface RemapResult {
  document: GraphDocument;
  nodeIdMap: ReadonlyMap<string, string>;
  edgeIdMap: ReadonlyMap<string, string>;
}

/**
 * Deterministic once a mapping is provided (graph-schema.md §9): generates a
 * fresh id for every node/edge, rewrites every edge endpoint and every
 * registered extension reference, and returns a document that must validate
 * cleanly against `validateGraphReferences`. ID generation is injectable
 * (tests / callers may supply a deterministic generator).
 */
export function remapGraphReferences(
  document: GraphDocument,
  options?: { createId?: () => string },
): RemapResult {
  const createId = options?.createId ?? (() => crypto.randomUUID());

  const nodeIdMap = new Map<string, string>();
  for (const node of document.nodes as NodeRecord[]) {
    nodeIdMap.set(node.id, createId());
  }
  const edgeIdMap = new Map<string, string>();
  for (const edge of document.edges as EdgeRecord[]) {
    edgeIdMap.set(edge.id, createId());
  }

  const nodes = (document.nodes as NodeRecord[]).map((node) => ({
    ...node,
    id: nodeIdMap.get(node.id)!,
  }));
  const edges = (document.edges as EdgeRecord[])
    .filter((edge) => nodeIdMap.has(edge.source) && nodeIdMap.has(edge.target))
    .map((edge) => ({
      ...edge,
      id: edgeIdMap.get(edge.id)!,
      source: nodeIdMap.get(edge.source)!,
      target: nodeIdMap.get(edge.target)!,
    }));

  const ctx: RemapContext = { nodeIdMap, edgeIdMap, createId };
  const next: GraphDocument = { ...document, nodes, edges };
  for (const [key, remapper] of Object.entries(EXTENSION_REMAPPERS)) {
    if (key in next) {
      next[key] = remapper(next[key], ctx);
    }
  }

  return { document: next, nodeIdMap, edgeIdMap };
}
